import cn from 'classnames';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import setVariableDataBindingOperation from 'editor/src/store/design/operation/setVariableDataBindingOperation';
import getVariableDataSummary from 'editor/src/store/design/selector/getVariableDataSummary';
import { VariableDataBinding } from 'editor/src/store/design/types';
import selectElementOperation from 'editor/src/store/editor/operation/selectElementOperation';
import { useDispatch, useSelector } from 'editor/src/store/hooks';
import getHostSetting from 'editor/src/store/hostSettings/selector/getHostSetting';

import { VariableDataCodeSet } from 'editor/src/lib/variableData/types';
import useTr from 'editor/src/util/useTr';

import IconCross from 'editor/src/component/Icon/IconCross';
import TextInput from 'editor/src/component/TextInput';

import styles from './index.module.scss';
import tabStyles from '../index.module.scss';

const NONE = 'none';
const ORDER_BATCH = 'order-batch';
const NO_CODE_SETS: VariableDataCodeSet[] = [];

/**
 * Where the design's variable data comes from — one statement for the whole
 * design, like Shared values. A field's own panel answers "which column does
 * THIS print"; this answers "which code set, and what does it contain".
 *
 * Gated on the host setting, except that a design already bound keeps the
 * section: an imported template drawing a code set must be readable and
 * unbindable wherever it is opened.
 */
function VariableDataSection() {
  const tr = useTr();
  const dispatch = useDispatch();
  const enabled = useSelector((state) => getHostSetting(state, 'enableVariableDataFields'));
  const codeSets = useSelector((state) => getHostSetting(state, 'variableDataCodeSets') ?? NO_CODE_SETS);
  const { binding, boundLayers } = useSelector(getVariableDataSummary);

  const [newColumn, setNewColumn] = useState('');

  const selectValue = useMemo(() => {
    if (!binding) {
      return NONE;
    }
    return binding.scope === 'orderBatch' ? ORDER_BATCH : binding.codeSetId ?? NONE;
  }, [binding]);

  const onSourceChange = useCallback(
    (value: string) => {
      if (value === NONE) {
        dispatch(setVariableDataBindingOperation(undefined));
        return;
      }
      // Ungated, "Not used" is the one move left: a bound template must stay
      // unbindable, but nothing new can be authored.
      if (!enabled) {
        return;
      }
      if (value === ORDER_BATCH) {
        // Keep the columns the author already named: switching a store bank to a
        // buyer upload is a change of source, not a reason to lose the schema.
        dispatch(
          setVariableDataBindingOperation({
            scope: 'orderBatch',
            columns: binding?.columns ?? [],
            sampleRow: binding?.sampleRow ?? {},
          }),
        );
        return;
      }
      const codeSet = codeSets.find((set: VariableDataCodeSet) => set.id === value);
      if (!codeSet) {
        return;
      }
      dispatch(
        setVariableDataBindingOperation({
          scope: codeSet.scope,
          codeSetId: codeSet.id,
          codeSetName: codeSet.name,
          columns: [...codeSet.columns],
          sampleRow: { ...codeSet.sampleRow },
        }),
      );
    },
    [binding, codeSets, enabled, dispatch],
  );

  const updateBinding = useCallback(
    (next: VariableDataBinding) => dispatch(setVariableDataBindingOperation(next)),
    [dispatch],
  );

  const onAddColumn = useCallback(() => {
    const column = newColumn.trim();
    if (!binding || !column || binding.columns.includes(column)) {
      return;
    }
    updateBinding({
      ...binding,
      columns: [...binding.columns, column],
      sampleRow: { ...binding.sampleRow, [column]: '' },
    });
    setNewColumn('');
  }, [binding, newColumn, updateBinding]);

  const onRemoveColumn = useCallback(
    (column: string) => {
      if (!binding) {
        return;
      }
      const { [column]: dropped, ...sampleRow } = binding.sampleRow;
      updateBinding({ ...binding, columns: binding.columns.filter((c) => c !== column), sampleRow });
    },
    [binding, updateBinding],
  );

  // Typing is held here and committed on blur. Every commit is an undo entry and
  // a sweep of every element in the design, so one per keystroke-pause would bury
  // the author's real edits under a stack of half-typed samples.
  const typedSamples = useRef<Record<string, string>>({});

  const onSampleTyped = useCallback((column: string, value: string) => {
    typedSamples.current[column] = value;
  }, []);

  const onSampleCommit = useCallback(
    (column: string) => {
      const typed = typedSamples.current[column];
      delete typedSamples.current[column];
      if (!enabled || !binding || typed === undefined || typed === (binding.sampleRow[column] ?? '')) {
        return;
      }
      updateBinding({ ...binding, sampleRow: { ...binding.sampleRow, [column]: typed } });
    },
    [binding, enabled, updateBinding],
  );

  if (!enabled && !binding) {
    return null;
  }

  const isOrderBatch = binding?.scope === 'orderBatch';

  return (
    <div className={cn(styles.VariableDataSection, 'cy-variable-data-section')}>
      <div className={cn(tabStyles.personalizedLayerHeader, styles.title)}>
        {tr('editor-variable-data-title', 'Variable data')}
      </div>
      <div className={cn(styles.note, 'cy-variable-data-intro')}>
        {tr(
          'editor-variable-data-intro',
          'Print a different value on every unit — activation codes, serial numbers, personal QR links. Bind a text or a QR field to a column below; production fills one row per printed item.',
        )}
      </div>

      {/* Ungated, the select still shows the source in effect and still reaches
          "Not used", but every other source is closed. */}
      <select
        className={cn(styles.select, 'cy-variable-data-source')}
        value={selectValue}
        onChange={(e) => onSourceChange(e.target.value)}
      >
        <option value={NONE}>{tr('editor-variable-data-source-none', 'Not used')}</option>
        <option value={ORDER_BATCH} disabled={!enabled && selectValue !== ORDER_BATCH}>
          {tr('editor-variable-data-source-order-batch', 'Customer uploads a list at checkout')}
        </option>
        {codeSets.length > 0 && (
          <optgroup label={tr('editor-variable-data-store-code-sets', 'Code sets in this store')}>
            {codeSets.map((set: VariableDataCodeSet) => (
              <option key={set.id} value={set.id} disabled={!enabled && selectValue !== set.id}>
                {set.name} · {tr('editor-variable-data-code-set-rows', '{{count}} rows', { count: set.rowCount })}
              </option>
            ))}
          </optgroup>
        )}
        {binding?.codeSetId && !codeSets.some((set: VariableDataCodeSet) => set.id === binding.codeSetId) && (
          <option value={binding.codeSetId} disabled>
            {binding.codeSetName ?? binding.codeSetId}
          </option>
        )}
      </select>

      {binding && (
        <div className={cn(styles.card, 'cy-variable-data-card')}>
          <div className={styles.cardTitle}>
            {isOrderBatch
              ? tr('editor-variable-data-columns-order-batch', 'Columns the customer’s list must contain')
              : tr('editor-variable-data-columns-sample-row', 'Columns · sample row')}
          </div>
          {binding.columns.length === 0 && (
            <div className={cn(styles.note, 'cy-variable-data-empty')}>
              {tr(
                'editor-variable-data-columns-empty',
                'Add the column names the uploaded list will have, e.g. code, url.',
              )}
            </div>
          )}
          {binding.columns.map((column) => (
            <div key={column} className={cn(styles.columnRow, 'cy-variable-data-column')}>
              <span className={styles.columnName}>{column}</span>
              {enabled ? (
                <TextInput
                  value={binding.sampleRow[column] ?? ''}
                  onChange={(value) => onSampleTyped(column, value)}
                  onBlur={() => onSampleCommit(column)}
                  className={cn(styles.sampleInput, 'cy-variable-data-sample')}
                  placeholder={tr('editor-variable-data-sample-value', 'Sample value')}
                  debounceTime={0}
                />
              ) : (
                <span className={cn(styles.sampleValue, 'cy-variable-data-sample-value')}>
                  {binding.sampleRow[column] ?? ''}
                </span>
              )}
              {isOrderBatch && enabled && (
                <button
                  type="button"
                  className={cn(styles.remove, 'cy-variable-data-remove-column')}
                  onClick={() => onRemoveColumn(column)}
                  aria-label={tr('editor-variable-data-remove-column', 'Remove column')}
                  title={tr('editor-variable-data-remove-column', 'Remove column')}
                >
                  <IconCross className={styles.removeIcon} />
                </button>
              )}
            </div>
          ))}
          {isOrderBatch && enabled && (
            <div className={styles.addRow}>
              <TextInput
                value={newColumn}
                onChange={setNewColumn}
                onEnterPressed={onAddColumn}
                className={cn(styles.sampleInput, 'cy-variable-data-new-column')}
                placeholder={tr('editor-variable-data-new-column', 'New column name')}
                debounceTime={0}
              />
              <button type="button" className={cn(styles.add, 'cy-variable-data-add-column')} onClick={onAddColumn}>
                {tr('Add', 'Add')}
              </button>
            </div>
          )}

          <div className={styles.cardTitle}>{tr('editor-variable-data-bound-layers', 'Bound layers')}</div>
          {boundLayers.length === 0 ? (
            <div className={cn(styles.note, 'cy-variable-data-unbound')}>
              {tr(
                'editor-variable-data-bound-layers-empty',
                'Nothing yet. Open a personalized text and set its value source to a column, or map a QR field to one.',
              )}
            </div>
          ) : (
            <div className={styles.chips}>
              {boundLayers.map(({ uuid, name, column, maxCharCount }) => (
                <button
                  key={`${uuid}-${column}`}
                  type="button"
                  className={cn(
                    styles.chip,
                    { [styles.chipWarning]: maxCharCount !== undefined },
                    'cy-variable-data-bound',
                  )}
                  onClick={() => dispatch(selectElementOperation(uuid))}
                >
                  {name} · {column}
                </button>
              ))}
            </div>
          )}
          {boundLayers.map(
            ({ uuid, name, column, maxCharCount, sampleLength }) =>
              maxCharCount !== undefined && (
                <div key={`${uuid}-${column}`} className={cn(styles.warning, 'cy-variable-data-cap-warning')}>
                  {tr(
                    'editor-variable-data-cap-too-short',
                    '“{{name}}” allows {{max}} characters, but the sample has {{count}}. Rows longer than {{max}} characters will be refused — raise its character limit.',
                    { name, max: maxCharCount, count: sampleLength ?? 0 },
                  )}
                </div>
              ),
          )}
          <div className={styles.note}>
            {isOrderBatch
              ? tr(
                  'editor-variable-data-order-batch-note',
                  'At checkout the customer uploads a list with these columns. Quantity is the number of rows — one row per printed item, nothing reused.',
                )
              : tr(
                  'editor-variable-data-store-bank-note',
                  'Codes are drawn from this set, one per printed item. Each code is used only once.',
                )}
          </div>
        </div>
      )}
    </div>
  );
}

export default React.memo(VariableDataSection);
